import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, Suspense } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
// NOTE: Avoid static import of SessionExpiryModal to prevent a circular dependency.
// The modal itself imports this context, so we lazy-load it only when needed.
// This preserves existing behavior while eliminating the static cycle flagged by Sonar.
// (Tests that query for the dialog still work because the dynamic import resolves immediately.)

export interface SessionTimeoutConfig {
  /** Total inactivity limit in ms (default 45m) */
  inactivityLimitMs?: number;
  /** Show warning this long before expiry (default 15m) */
  warningBeforeMs?: number;
  /** Alternatively, show warning after this fixed inactivity duration (overrides warningBeforeMs logic if provided) */
  warningInactivityThresholdMs?: number;
  /** Throttle activity timestamp writes (default 10s) */
  activityWriteThrottleMs?: number;
}

export interface SessionTimeoutContextValue {
  /** ms remaining until automatic logout */
  remainingMs: number;
  /** Whether the warning dialog is currently visible */
  isWarningVisible: boolean;
  /** Manually extend (reset) the session */
  extendSession: () => void;
  /** Force expire immediately */
  expireNow: () => void;
  /** Open warning manually (for testing) */
  showWarning: () => void;
  /** Raw config in effect */
  config: Required<Omit<SessionTimeoutConfig, 'warningInactivityThresholdMs'>> & Pick<SessionTimeoutConfig, 'warningInactivityThresholdMs'>;
}

// Production defaults per requirements: 45m total inactivity, warn at T-15m.
// (Shorter values can be injected via props for local testing.)
const DEFAULTS: Required<Omit<SessionTimeoutConfig, 'warningInactivityThresholdMs'>> = {
  inactivityLimitMs: 45 * 60 * 1000,
  warningBeforeMs: 15 * 60 * 1000,
  activityWriteThrottleMs: 10 * 1000,
};

const SessionTimeoutContext = createContext<SessionTimeoutContextValue | undefined>(undefined);

// Dev-only global helpers declaration (optional usage in browser console for QA)
declare global {
  interface Window {
    showSessionWarning?: () => void;
    extendSession?: () => void;
    expireSessionNow?: () => void;
  }
}

interface ProviderProps extends SessionTimeoutConfig { children: React.ReactNode }

export const SessionTimeoutProvider: React.FC<ProviderProps> = ({
  children,
  inactivityLimitMs,
  warningBeforeMs,
  warningInactivityThresholdMs,
  activityWriteThrottleMs,
}) => {
  const config = useMemo(() => ({
    inactivityLimitMs: inactivityLimitMs ?? DEFAULTS.inactivityLimitMs,
    warningBeforeMs: warningBeforeMs ?? DEFAULTS.warningBeforeMs,
    activityWriteThrottleMs: activityWriteThrottleMs ?? DEFAULTS.activityWriteThrottleMs,
    warningInactivityThresholdMs,
  }), [inactivityLimitMs, warningBeforeMs, activityWriteThrottleMs, warningInactivityThresholdMs]);
  const { logout } = useAuth();
  const navigate = useNavigate();

  const lastActivityRef = useRef<number>(Date.now());
  const lastPersistRef = useRef<number>(lastActivityRef.current);
  const [now, setNow] = useState<number>(() => Date.now());
  // State for warning visibility
  const [isWarningVisible, setWarningVisible] = useState<boolean>(false); // eslint-disable-line
  const tickIntervalRef = useRef<number | null>(null);

  // Compute inactivity duration and remaining time (more explicit & aligns with requirement wording)
  const inactiveForMs = Math.max(0, now - lastActivityRef.current);
  const remainingMs = Math.max(0, config.inactivityLimitMs - inactiveForMs);

  const showWarning = useCallback(() => {
    if (!isWarningVisible) setWarningVisible(true);
  }, [isWarningVisible]);

  const hideWarning = useCallback(() => setWarningVisible(false), []);

  const extendSession = useCallback(() => {
    lastActivityRef.current = Date.now();
    hideWarning();
    // Force immediate recompute
    setNow(Date.now());
  }, [hideWarning]);

  const expireNow = useCallback(() => {
    hideWarning();
    logout();
    navigate('/error/session-expired', { replace: true });
  }, [hideWarning, logout, navigate]);

  // Activity handler (throttled writes)
  const recordActivity = useCallback(() => {
    const t = Date.now();
    if (t - lastActivityRef.current < config.activityWriteThrottleMs) return; // throttle
    lastActivityRef.current = t;
    // Only update "now" state if warning showing (to recalc countdown) or occasionally for accuracy
    if (isWarningVisible || t - now > 15_000) {
      setNow(t);
    }
  }, [config.activityWriteThrottleMs, isWarningVisible, now]);

  // Attach global listeners once
  useEffect(() => {
    // Treat genuine user interaction as activity (exclude visibilitychange so simply refocusing tab doesn't extend session)
    const activityEvents = ['mousemove','mousedown','keydown','scroll','touchstart'];
    const activityHandler = () => {
      if (document.visibilityState === 'hidden') return;
      recordActivity();
    };
    activityEvents.forEach(e => window.addEventListener(e, activityHandler, { passive: true }));

    // Separate visibility handler: when tab becomes visible, update 'now' so thresholds are reevaluated without resetting lastActivity
    const visibilityHandler = () => {
      if (document.visibilityState === 'visible') {
        setNow(Date.now());
      }
    };
    document.addEventListener('visibilitychange', visibilityHandler);

    // Dev keyboard shortcut: Alt+Shift+S to show warning immediately
    const debugKey = (e: KeyboardEvent) => {
      if (e.altKey && e.shiftKey && (e.key === 'S' || e.key === 's')) {
        showWarning();
      }
    };
    window.addEventListener('keydown', debugKey);

    return () => {
      activityEvents.forEach(e => window.removeEventListener(e, activityHandler));
      document.removeEventListener('visibilitychange', visibilityHandler);
      window.removeEventListener('keydown', debugKey);
    };
  }, [recordActivity, showWarning]);

  // Always run a 1s interval to update 'now' for robust modal triggering
  useEffect(() => {
    const updateNow = () => setNow(Date.now());
    tickIntervalRef.current = window.setInterval(updateNow, 1000);
    return () => {
      if (tickIntervalRef.current) {
        clearInterval(tickIntervalRef.current);
        tickIntervalRef.current = null;
      }
    };
  }, []);

  // Show warning either at explicit fixed inactivity threshold OR derived from limit - warningBefore.
  useEffect(() => {
    const derivedThreshold = config.inactivityLimitMs - config.warningBeforeMs;
    const threshold = config.warningInactivityThresholdMs ?? derivedThreshold;
    if (!isWarningVisible && inactiveForMs >= threshold && inactiveForMs < config.inactivityLimitMs) {
      showWarning();
    }
  }, [inactiveForMs, isWarningVisible, showWarning, config.inactivityLimitMs, config.warningBeforeMs, config.warningInactivityThresholdMs]);

  // Auto-expire
  useEffect(() => {
    if (remainingMs <= 0) {
      expireNow();
    }
  }, [remainingMs, expireNow]);

  // Occasionally persist last activity to localStorage (optional future cross-reload). Write at most every 60s
  useEffect(() => {
    const t = Date.now();
    if (t - lastPersistRef.current > 60_000) {
      try { localStorage.setItem('lastActivityTs', String(lastActivityRef.current)); } catch {}
      lastPersistRef.current = t;
    }
  }, [now]);

  const value: SessionTimeoutContextValue = useMemo(() => ({
    remainingMs,
    isWarningVisible,
    extendSession,
    expireNow,
    showWarning,
    config,
  }), [remainingMs, isWarningVisible, extendSession, expireNow, showWarning, config]);

  // Expose debug helpers in development to allow manual triggering via console.
  // We intentionally avoid referencing `import.meta` so this parses in Jest CJS.
  const IS_DEV = useMemo(
    () => (typeof process !== 'undefined' && process?.env?.NODE_ENV
      ? process.env.NODE_ENV !== 'production'
      : false),
    []
  );

  useEffect(() => {
    if (!IS_DEV) return;
    window.showSessionWarning = showWarning;
    window.extendSession = extendSession;
    window.expireSessionNow = expireNow;
    return () => {
      delete window.showSessionWarning;
      delete window.extendSession;
      delete window.expireSessionNow;
    };
  }, [IS_DEV, showWarning, extendSession, expireNow]);

  // Lazy component memoized so we don't create multiple lazy wrappers across renders.
  const LazySessionExpiryModal = useMemo(
    () => React.lazy(() => import('../components/common/SessionExpiryModal')),
    []
  );

  const modal = isWarningVisible
    ? createPortal(
        <Suspense fallback={null}>
          <LazySessionExpiryModal />
        </Suspense>,
        document.body
      )
    : null;

  return (
    <SessionTimeoutContext.Provider value={value}>
      {children}
      {modal}
    </SessionTimeoutContext.Provider>
  );
};

export const useSessionTimeout = () => useContext(SessionTimeoutContext);
